import {
  quat,
  ReadonlyMat4,
  ReadonlyVec3,
  vec3,
  vec4,
  glMatrix,
} from "gl-matrix";
import { clampNumber } from "./misc";

export type vecmat3 = [vec3, vec3, vec3];

glMatrix.setMatrixArrayType(Array);

export function basisFromRot(basis: vecmat3, mat: ReadonlyMat4) {
  vec3.set(basis[0], mat[0 + 0 * 4], mat[1 + 0 * 4], mat[2 + 0 * 4]);
  vec3.set(basis[1], mat[0 + 1 * 4], mat[1 + 1 * 4], mat[2 + 1 * 4]);
  vec3.set(basis[2], mat[0 + 2 * 4], mat[1 + 2 * 4], mat[2 + 2 * 4]);
}

export function vec3Random(length = 1): vec3 {
  const v = vec3.fromValues(
    Math.random() - 0.5,
    Math.random() - 0.5,
    Math.random() - 0.5
  );
  vec3.normalize(v, v);

  if (length !== 1) {
    vec3.scale(v, v, length);
  }

  return v;
}

export function quatTop(q: quat): quat {
  if (q[3] < 0) vec4.negate(q, q);
  return q;
}

export function quatAngle(q: quat): number {
  return 2 * Math.acos(clampNumber(q[3], -1, 1));
}

export function getAverage<T>(
  objects: T[],
  access: (object: T) => ReadonlyVec3 = (p) => p as unknown as vec3,
  target?: vec3
): vec3 | undefined {
  const average = target ? vec3.zero(target) : vec3.fromValues(0, 0, 0);
  for (const p of objects) {
    vec3.add(average, average, access(p));
  }

  return objects.length > 0
    ? vec3.scale(average, average, 1 / objects.length)
    : undefined;
}

/**
 * Returns an euler angle representation of a quaternion
 * https://github.com/toji/gl-matrix/issues/329
 * @param  {vec3} out Euler angles, pitch-yaw-roll
 * @param  {quat} mat Quaternion
 * @return {vec3} out
 */
export function getEuler(out: vec3, q: quat) {
  const x = q[0];
  const y = q[1];
  const z = q[2];
  const w = q[3];

  const x2 = x * x;
  const y2 = y * y;
  const z2 = z * z;
  const w2 = w * w;

  const unit = x2 + y2 + z2 + w2;
  const test = x * w - y * z;
  if (test > 0.499995 * unit) {
    // TODO: Use glmatrix.EPSILON
    // singularity at the north pole
    out[0] = Math.PI / 2;
    out[1] = 2 * Math.atan2(y, x);
    out[2] = 0;
  } else if (test < -0.499995 * unit) {
    // TODO: Use glmatrix.EPSILON
    // singularity at the south pole
    out[0] = -Math.PI / 2;
    out[1] = 2 * Math.atan2(y, x);
    out[2] = 0;
  } else {
    out[0] = Math.asin(2 * (x * z - w * y));
    out[1] = Math.atan2(2 * (x * w + y * z), 1 - 2 * (z2 + w2));
    out[2] = Math.atan2(2 * (x * y + z * w), 1 - 2 * (y2 + z2));
  }
  // TODO: Return them as degrees and not as radians
  return out;
}

export function quatFromOrderedEuler(
  q: quat,
  euler: ReadonlyVec3,
  order: string = "XYZ"
): quat | undefined {
  const x = euler[0];
  const y = euler[1];
  const z = euler[2];

  // http://www.mathworks.com/matlabcentral/fileexchange/
  // 	20696-function-to-convert-between-dcm-euler-angles-quaternions-and-euler-vectors/
  // 	content/SpinCalc.m

  const cos = Math.cos;
  const sin = Math.sin;

  const c1 = cos(x / 2);
  const c2 = cos(y / 2);
  const c3 = cos(z / 2);

  const s1 = sin(x / 2);
  const s2 = sin(y / 2);
  const s3 = sin(z / 2);

  let success = true;
  switch (order.toUpperCase()) {
    case "XYZ":
      q[0] = s1 * c2 * c3 + c1 * s2 * s3;
      q[1] = c1 * s2 * c3 - s1 * c2 * s3;
      q[2] = c1 * c2 * s3 + s1 * s2 * c3;
      q[3] = c1 * c2 * c3 - s1 * s2 * s3;
      break;

    case "YXZ":
      q[0] = s1 * c2 * c3 + c1 * s2 * s3;
      q[1] = c1 * s2 * c3 - s1 * c2 * s3;
      q[2] = c1 * c2 * s3 - s1 * s2 * c3;
      q[3] = c1 * c2 * c3 + s1 * s2 * s3;
      break;

    case "ZXY":
      q[0] = s1 * c2 * c3 - c1 * s2 * s3;
      q[1] = c1 * s2 * c3 + s1 * c2 * s3;
      q[2] = c1 * c2 * s3 + s1 * s2 * c3;
      q[3] = c1 * c2 * c3 - s1 * s2 * s3;
      break;

    case "ZYX":
      q[0] = s1 * c2 * c3 - c1 * s2 * s3;
      q[1] = c1 * s2 * c3 + s1 * c2 * s3;
      q[2] = c1 * c2 * s3 - s1 * s2 * c3;
      q[3] = c1 * c2 * c3 + s1 * s2 * s3;
      break;

    case "YZX":
      q[0] = s1 * c2 * c3 + c1 * s2 * s3;
      q[1] = c1 * s2 * c3 + s1 * c2 * s3;
      q[2] = c1 * c2 * s3 - s1 * s2 * c3;
      q[3] = c1 * c2 * c3 - s1 * s2 * s3;
      break;

    case "XZY":
      q[0] = s1 * c2 * c3 - c1 * s2 * s3;
      q[1] = c1 * s2 * c3 - s1 * c2 * s3;
      q[2] = c1 * c2 * s3 + s1 * s2 * c3;
      q[3] = c1 * c2 * c3 + s1 * s2 * s3;
      break;

    default:
      console.error(`unknown order: ${order}`);
      success = false;
      break;
  }

  return success ? q : undefined;
}

export function quatAlmostEqual(
  a: quat,
  b: quat,
  epsilon = 0.000000001
): boolean {
  let diff = 0;
  for (let i = 0; i < a.length; i++) {
    diff += Math.abs(a[i] - b[i]);
  }

  const equal = diff < epsilon;
  if (!equal) console.log(diff);
  return equal;
}

export function quatBetween(a: vec3, b: vec3): quat {
  const q = quat.create();
  const dot = vec3.dot(a, b);
  if (dot > 0.999999) {
    return q;
  } else if (dot < -0.999999) {
    quat.invert(q, q);
    return q;
  }

  const cross = vec3.create();
  vec3.cross(cross, a, b);

  q[0] = cross[0];
  q[1] = cross[1];
  q[2] = cross[2];
  q[3] = Math.sqrt(vec3.squaredLength(a) * vec3.squaredLength(b)) + dot;
  quat.normalize(q, q);

  return q;
}

// https://theorangeduck.com/page/joint-limits
export function softenProjection(
  out: vec3,
  from: ReadonlyVec3,
  to: ReadonlyVec3,
  falloff = 1,
  radius = 0.1,
  eps = 1e-5
): void {
  const distance = vec3.distance(from, to);

  if (distance > eps) {
    // Compute how much softening to apply up to `radius`
    const softening = Math.tanh(falloff * distance) * radius;

    // Add softening toward original position
    vec3.lerp(out, from, to, 1 / softening);
  } else {
    vec3.copy(out, to);
  }
}
